<template>
  <div class="edit-tag" :class="statusClass">
    <div v-if="isSavingValue" class="d-flex align-center ga-2">
      <v-progress-circular indeterminate size="16" width="2" color="warning" />
      <span class="edit-tag__text">{{ t("saving") }}</span>
    </div>

    <div v-else-if="hasError" class="d-flex align-center ga-2">
      <v-icon icon="mdi-alert-circle" color="error" size="small" />
      <span class="edit-tag__text">
        {{ t("saveError") }}
        <span v-if="errorList.length > 0" class="text-caption ml-1">
          ({{ errorList[0].error.message }})
        </span>
      </span>
    </div>

    <div v-else-if="lastSaved" class="d-flex align-center ga-2">
      <v-icon icon="mdi-check-circle" color="success" size="small" />
      <span class="edit-tag__text">
        {{ t("lastEdit") }} {{ lastEditDate }} {{ t("lastEditAt") }}
        {{ lastEditTime }}
      </span>
    </div>

    <div v-else class="d-flex align-center ga-2">
      <v-icon icon="mdi-circle-outline" color="grey" size="small" />
      <span class="edit-tag__text">{{ t("ready") }}</span>
    </div>
  </div>
</template>
<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  isSaving: {
    type: [Boolean, Object],
    default: false,
  },
  isDirty: {
    type: [Boolean, Object],
    default: false,
  },
  lastSaved: {
    type: [Date, Object, String, Number],
    default: null,
  },
  autoSave: {
    type: Object,
    default: null,
  },
  hasErrors: {
    type: Boolean,
    default: false,
  },
  saveErrors: {
    type: Array,
    default: () => [],
  },
});

const isSavingValue = computed(() => {
  const saving =
    (props.isSaving?.value ?? props.isSaving) ||
    props.autoSave?.isSaving?.value ||
    props.autoSave?.isSaving;
  return Boolean(saving);
});

const errorList = computed(() => {
  if (props.saveErrors && props.saveErrors.length > 0) {
    return props.saveErrors;
  }

  try {
    const autoSaveErrors = props.autoSave?.saveErrors?.value || props.autoSave?.saveErrors || [];
    return Array.isArray(autoSaveErrors) ? autoSaveErrors : [];
  } catch {
    return [];
  }
});

const hasError = computed(() => {
  if (props.hasErrors) return true;
  return errorList.value.length > 0;
});

const lastSaved = computed(() => {
  const saved =
    (props.lastSaved?.value ?? props.lastSaved) ||
    props.autoSave?.lastSaved?.value ||
    props.autoSave?.lastSaved;
  return saved || null;
});

const statusClass = computed(() => ({
  "edit-tag--saving": isSavingValue.value,
  "edit-tag--error": hasError.value,
  "edit-tag--success": !isSavingValue.value && !hasError.value && lastSaved.value,
  "edit-tag--default": !isSavingValue.value && !hasError.value && !lastSaved.value,
}));

const lastEditDate = computed(() => {
  const saved = lastSaved.value;
  if (!saved) return "";

  const date = new Date(saved);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
});

const lastEditTime = computed(() => {
  const saved = lastSaved.value;
  if (!saved) return "";

  const date = new Date(saved);
  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
});
</script>

<i18n>
{
  "en": {
    "lastEdit": "Saved",
    "lastEditAt": "at",
    "saving": "Saving...",
    "saveError": "Save error",
    "ready": "Ready"
  },
  "fr": {
    "lastEdit": "Enregistré le",
    "lastEditAt": "à",
    "saving": "Sauvegarde...",
    "saveError": "Erreur de sauvegarde",
    "ready": "Prêt"
  }
}
</i18n>

<style scoped>
.edit-tag {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  min-width: 3.5em;
  transition: all 0.2s ease;

  .edit-tag__text {
    font-weight: 400;
    font-size: 12px;
    line-height: 15px;
  }

  .edit-tag__icon {
    margin: 0 0 0 8px;
  }
}

/* Success state - green */
.edit-tag--success {
  color: #00833f;
  background-color: #edf9f2;
}

/* Saving state - orange */
.edit-tag--saving {
  color: #e65100;
  background-color: #fff3e0;
}

/* Error state - red */
.edit-tag--error {
  color: #c62828;
  background-color: #ffebee;
}

/* Default state - grey */
.edit-tag--default {
  color: #757575;
  background-color: #f5f5f5;
}
</style>
